import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import Pagination from "@/components/base/Pagination";
import { worksList, regionOptions, yearOptions, genreOptions } from "@/mocks/works";

const sortOptions = [
  { key: "update", label: "按更新时间", icon: "ri-time-line" },
  { key: "heat", label: "按热度", icon: "ri-fire-line" },
  { key: "rating", label: "按评分", icon: "ri-star-line" },
];

const typeDesc: Record<string, string> = {
  电视剧: "最新电视剧推荐，涵盖古装、都市、悬疑、年代等热门题材，追更不停。",
  电影: "精选电影推荐，科幻、爱情、犯罪、励志佳作一网打尽。",
  综艺: "热播综艺合集，真人秀、竞技与音乐节目，看见真实的精彩。",
};

const PAGE_SIZE = 8;

export default function Category() {
  const { type = "电视剧" } = useParams();
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [genre, setGenre] = useState("全部");
  const [sort, setSort] = useState("update");
  const [page, setPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = worksList.filter((w) => w.type === type);
    if (region !== "全部") list = list.filter((w) => w.region === region);
    if (year !== "全部") list = list.filter((w) => String(w.year) === year);
    if (genre !== "全部") list = list.filter((w) => w.genre === genre);
    const sorted = [...list];
    if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else if (sort === "heat") sorted.sort((a, b) => b.heat - a.heat);
    else sorted.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    return sorted;
  }, [type, region, year, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const shown = paged.slice(0, visibleCount);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setGenre("全部");
    setSort("update");
    setPage(1);
    setVisibleCount(PAGE_SIZE);
  };

  const update = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
    setVisibleCount(PAGE_SIZE);
  };

  const FilterGroup = ({
    label,
    options,
    value,
    onSelect,
  }: {
    label: string;
    options: string[];
    value: string;
    onSelect: (v: string) => void;
  }) => (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 py-3 border-b border-background-200 last:border-0">
      <span className="shrink-0 w-14 text-sm text-foreground-500 pt-1">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onSelect(opt)}
            className={`px-3 py-1 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
              value === opt
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <Layout>
      {/* 页头 */}
      <section className="w-full px-4 md:px-8 pt-6 md:pt-10 pb-6 border-b border-background-200 bg-background-100">
        <Breadcrumb items={[{ label: type }]} />
        <div className="mt-4">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            {type} - 爱爱网影视推荐
          </h1>
          <p className="mt-2 text-sm text-foreground-600 max-w-3xl">
            {typeDesc[type] ?? "发现值得观看的影视作品。"}
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
          {/* 侧边筛选（桌面） */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  筛选条件
                </h2>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-xs text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  重置
                </button>
              </div>
              <FilterGroup label="地区" options={regionOptions} value={region} onSelect={update(setRegion)} />
              <FilterGroup label="年份" options={yearOptions} value={year} onSelect={update(setYear)} />
              <FilterGroup label="类型" options={genreOptions} value={genre} onSelect={update(setGenre)} />
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            {/* 排序栏 */}
            <div className="flex items-center justify-between gap-3 mb-5">
              <div className="text-sm text-foreground-600">
                共 <span className="text-primary-600 font-semibold">{filtered.length}</span> 部作品
              </div>
              <div className="flex items-end gap-2">
                <button
                  type="button"
                  onClick={() => setFilterOpen(true)}
                  className="lg:hidden inline-flex items-center gap-1 h-9 px-3 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-filter-3-line" />
                  筛选
                </button>
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => update(setSort)(opt.key)}
                      className={`px-3 py-1 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        sort === opt.key
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:text-primary-600"
                      }`}
                    >
                      <i className={`${opt.icon} mr-1`} />
                      <span className="hidden sm:inline">{opt.label}</span>
                      <span className="sm:hidden">{opt.label.replace("按", "")}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* 作品网格 */}
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mb-4">
                  <i className="ri-inbox-line text-3xl" />
                </span>
                <h3 className="font-heading text-lg font-semibold text-foreground-800">
                  暂无符合条件的影视作品
                </h3>
                <p className="mt-1 text-sm text-foreground-500">
                  试着调整筛选条件，或重置后重新浏览
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 inline-flex items-center gap-2 px-5 h-10 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line" />
                  重置筛选
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                  {shown.map((work) => (
                    <WorkCard key={work.id} work={work} />
                  ))}
                </div>

                {visibleCount < paged.length && (
                  <div className="flex justify-center mt-8">
                    <button
                      type="button"
                      onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 px-6 h-11 rounded-md border border-primary-300 text-primary-600 hover:bg-primary-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line" />
                      加载更多
                    </button>
                  </div>
                )}

                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </>
            )}
          </div>
        </div>
      </section>

      {/* 移动端筛选弹层 */}
      {filterOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setFilterOpen(false)}
          />
          <div className="relative w-full bg-background-50 rounded-t-2xl p-5 max-h-[80vh] overflow-y-auto animate-fade-up">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                筛选条件
              </h2>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-600 hover:bg-background-100 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterGroup label="地区" options={regionOptions} value={region} onSelect={update(setRegion)} />
            <FilterGroup label="年份" options={yearOptions} value={year} onSelect={update(setYear)} />
            <FilterGroup label="类型" options={genreOptions} value={genre} onSelect={update(setGenre)} />
            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 h-11 rounded-md border border-background-300 text-foreground-700 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}